import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil, Plus, AlertCircle, BarChart4 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { ResidueModalForm } from "./ResidueModalForm";
import { useData } from "@/hooks/useData";
import { ResidueDoc, TreatmentType, ResidueEntry } from "@/types/residue";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { calculateResidueFootprint, calculateTotalResidueFootprint } from "../../utils/calculations/annualConsumptions";
import { formatNumber } from "@/utils/numberFormatter";

// Extended ResidueEntry to always include a footprint
export interface ResidueEntryWithFootprint extends ResidueEntry {
  pc_res: number;
}

export interface ResiduePayload {
  entries: ResidueEntryWithFootprint[];
  total_pc_res: number;
}

interface ResidueStepProps {
  existingData?: ResiduePayload;
  onDataChange: (payload: ResiduePayload) => void;
  showTitle?: boolean;
}

const emptyEntry: ResidueEntry = { residueType: "", treatments: [] };

// Ensure each residue doc has an `id`
type ResidueDocWithId = ResidueDoc & { id: string };

export const ResidueStep = ({ existingData, onDataChange, showTitle = true }: ResidueStepProps) => {
  const { t } = useLanguage();
  // Initialize entries array from existingData.entries, if provided
  const [residueEntries, setResidueEntries] = useState(
    Array.isArray(existingData?.entries) ? existingData!.entries : []
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<ResidueEntry>(emptyEntry);
  const [treatmentOptions, setTreatmentOptions] = useState<{ value: string; label: string; fe_r?: number }[]>([]);

  // Fetch residue docs
  const { data: residueDocs } = useData<ResidueDocWithId>({
    collectionName: "setup_residues",
  });

  // Get all residue options and filter only when adding new entries
  const getAllResidueOptions = () => {
    return (residueDocs || []).map((doc) => ({ value: doc.name, label: doc.name }));
  };

  // Get residue options for the modal form
  // When editing, include the current type; when adding, exclude types already in use
  const getResidueOptions = () => {
    const allOptions = getAllResidueOptions();

    if (editingIndex !== null) {
      // When editing, return all options or just the current residue type if it's the only option
      return allOptions;
    } else {
      // When adding, filter out types already in use
      return allOptions.filter((opt) => !residueEntries.some((e) => e.residueType === opt.value));
    }
  };

  // Update treatment options when picking a residueType
  useEffect(() => {
    if (formEntry.residueType && residueDocs) {
      const selected = residueDocs.find((doc) => doc.name === formEntry.residueType);
      if (selected?.treatment_types) {
        setTreatmentOptions(
          selected.treatment_types.map((treatment: TreatmentType) => ({
            value: treatment.id,
            label: t(`footprints.residue.treatments.${treatment.id}`) || treatment.id,
            fe_r: treatment.fe_r,
          }))
        );
      } else {
        setTreatmentOptions([]);
      }
    } else {
      setTreatmentOptions([]);
    }
  }, [formEntry.residueType, residueDocs, t]);

  // Sync up if existingData changes
  useEffect(() => {
    const entries = Array.isArray(existingData?.entries) ? existingData.entries : [];
    setResidueEntries(entries);
  }, [existingData]);

  /** Provides { label, fe_r } for a given treatmentId and residueType */
  const getTreatmentInfoForEntry = (residueType: string, treatmentId: string): { label: string; fe_r?: number } | undefined => {
    const currentResidueDoc = residueDocs?.find((doc) => doc.name === residueType);

    if (currentResidueDoc?.treatment_types) {
      const found = currentResidueDoc.treatment_types.find((t) => t.id === treatmentId);
      if (found) {
        return {
          label: t(`footprints.residue.treatments.${treatmentId}`) || treatmentId,
          fe_r: found.fe_r,
        };
      }
    }
    return undefined;
  };

  // Recalculate footprints when residueDocs are loaded or changed
  useEffect(() => {
    if (residueDocs && residueDocs.length > 0 && residueEntries.length > 0) {
      let hasChanges = false;

      const updatedEntries = residueEntries.map(entry => {
        // Skip entries without treatments
        if (!entry.treatments || entry.treatments.length === 0) {
          return entry;
        }

        // Always recalculate using current residue doc data
        const newFootprint = calculateResidueFootprint(entry, (treatmentId: string) =>
          getTreatmentInfoForEntry(entry.residueType, treatmentId)
        );

        // Check if value changed
        if (entry.pc_res !== newFootprint) {
          hasChanges = true;
          return {
            ...entry,
            pc_res: newFootprint,
          };
        }

        return entry;
      });

      // Only update if something actually changed
      if (hasChanges) {
        setResidueEntries(updatedEntries);
        onDataChange({
          entries: updatedEntries,
          total_pc_res: calculateTotalResidueFootprint(updatedEntries),
        });
      }
    }
  }, [residueDocs, residueEntries, t, onDataChange]);

  /** Open form modal (add vs edit) */
  const openModal = (entry?: ResidueEntryWithFootprint, idx?: number) => {
    if (entry && typeof idx === "number") {
      setFormEntry({ ...entry, treatments: [...entry.treatments] });
      setEditingIndex(idx);
    } else {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
      setTreatmentOptions([]);
    }, 100);
  };

  /** Provides { label, fe_r } for a given treatmentId */
  const getTreatmentInfo = (treatmentId: string): { label: string; fe_r?: number } | undefined => {
    const currentResidueDoc = residueDocs?.find((doc) => doc.name === formEntry.residueType);
    
    if (currentResidueDoc?.treatment_types) {
      const found = currentResidueDoc.treatment_types.find((t) => t.id === treatmentId);
      if (found) {
        return {
          label: t(`footprints.residue.treatments.${treatmentId}`) || treatmentId,
          fe_r: found.fe_r,
        };
      }
    }
    return undefined;
  };

  /** Save (add/edit) */
  const handleSave = () => {
    const entryWithFootprint: ResidueEntryWithFootprint = {
      ...formEntry,
      pc_res: calculateResidueFootprint(formEntry, getTreatmentInfo),
    };

    const newEntries = [...residueEntries];
    if (editingIndex !== null) newEntries[editingIndex] = entryWithFootprint;
    else newEntries.push(entryWithFootprint);

    setResidueEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_res: calculateTotalResidueFootprint(newEntries),
    });
    closeModal();
  };

  /** Remove one entry */
  const handleRemoveEntry = (index: number) => {
    const newEntries = residueEntries.filter((_, i) => i !== index);
    setResidueEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_res: calculateTotalResidueFootprint(newEntries),
    });
  };

  /** Pretty-label lookup */
  const getTreatmentLabel = (treatmentId: string): string => {
    for (const residue of residueDocs || []) {
      const found = residue.treatment_types?.find((t) => t.id === treatmentId);
      if (found) {
        return t(`footprints.residue.treatments.${treatmentId}`) || treatmentId;
      }
    }
    return treatmentId;
  };

  const handleFormEntryChange = (entry: ResidueEntry) => {
    setFormEntry(entry);
  };

  return (
    <div className="space-y-6">
      {/* Title + Add button */}
      {showTitle ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t("footprints.residue.title")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("footprints.residue.listTitle")}</p>
          </div>
          <Button
            onClick={() => openModal()}
            variant="outline"
            className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
          >
            <Plus className="h-4 w-4" />
            {t("footprints.residue.addEntry")}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end mb-4">
          <Button
            onClick={() => openModal()}
            variant="outline"
            className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
          >
            <Plus className="h-4 w-4" />
            {t("footprints.residue.addEntry")}
          </Button>
        </div>
      )}

      {/* No entries / list of cards */}
      {residueEntries.length === 0 ? (
        <Alert variant="default" className="bg-gray-50 border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
            <AlertDescription className="text-gray-500">{t("footprints.residue.noEntries")}</AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {residueEntries.map((entry, idx) => (
              <Card key={idx} className="flex flex-col h-full shadow-sm hover:shadow transition-shadow">
                <CardHeader className="bg-gray-50 pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold">{entry.residueType}</CardTitle>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openModal(entry, idx)}>
                        <Pencil className="h-4 w-4 text-gray-500 hover:text-teal-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveEntry(idx)}>
                        <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  {entry.treatments.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      {entry.treatments.map((t, i) => (
                        <li key={i} className="py-2 flex justify-between items-center">
                          <span>{getTreatmentLabel(t.treatmentTypeId)}</span>
                          <Badge variant="secondary">{formatNumber(Number(t.quantity))} t</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="py-4 text-center text-gray-500">{t("footprints.residue.noTreatmentsAdded")}</div>
                  )}
                </CardContent>
                <CardFooter className="bg-teal-50 border-t py-3 px-4">
                  <div className="flex justify-between items-center text-teal-700 w-full">
                    <div className="flex items-center gap-1">
                      <BarChart4 className="h-4 w-4 align-middle" />
                      <span className="align-middle">{t("footprints.residue.carbonFootprint")}</span>
                    </div>

                    <div className="flex items-baseline gap-1">
                      <span className="text-lg font-bold leading-none">{formatNumber(entry.pc_res)}</span>
                      <span className="text-xs">
                        {t("dashboard.company.kgCO2")}
                      </span>
                    </div>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Total Footprint */}
          <div className="mt-6 rounded-lg border border-teal-200 bg-white shadow-sm">
            <div className="flex justify-between items-center py-5 px-6">
              <div>
                <div className="flex items-center gap-2 text-teal-700">
                  <BarChart4 className="h-5 w-5" />
                  <span>{t("footprints.residue.totalFootprint")}</span>
                </div>
                <p className="ml-7 mt-2 text-xs italic text-teal-600">{t("footprints.residue.footprintFormula")}</p>
              </div>
              <span className="text-lg font-bold text-teal-700">
                {formatNumber(calculateTotalResidueFootprint(residueEntries))} {t("dashboard.company.kgCO2")}
              </span>
            </div>
          </div>
        </>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <ResidueModalForm
          onClose={closeModal}
          onSave={handleSave}
          formEntry={formEntry}
          onFormEntryChange={handleFormEntryChange}
          residueOptions={getResidueOptions()}
          treatmentOptions={treatmentOptions}
          editing={editingIndex !== null}
        />
      </Dialog>
    </div>
  );
};
